import { uniqBy } from 'lodash';
import React, { FunctionComponent } from 'react';
import { Row } from 'react-styled-flexboxgrid';
import { useMedia } from 'react-use';

import { media, NOTIFICATION_TYPES } from '@savgroup-front-common/constants';
import { Banner } from '@savgroup-front-common/core/atoms/Banners';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import { AddressInfoDto, PickupPoint } from '@savgroup-front-common/types';
import { Carrier } from '@/api/Claim/getClaimGroupCarrierQuery';

import Address from './Address';
import PickupPointList from './components/PickupPointList';
import PickupPointMap from './components/PickupPointMap';
import messages from './messages';
import {
  $AddressContainer,
  $ColListContainer,
  $ColMapContainer,
  $NoPickupPoint,
  $SelectContainerGrid,
  $SelectContainerRow,
} from './PickupPointSelector.styles';
import { AddressSelected, PickupPoints } from './PickupPointSelector.types';

interface PickupPointSelectorProps {
  pickupPoints: PickupPoints;
  maxDistance: number;
  onAddressSelected: ({ address, countryCode }: AddressSelected) => void;
  searchAddress?: AddressSelected;
  userAddresses: AddressInfoDto[];
  pickupPointsIsLoading: boolean;
  carriers?: Carrier[];
  onSelectPickupPoint: (payload: PickupPoint) => void;
  selectedPickupPointId?: string;
  sellerId?: string;
  noPickupPoints?: boolean;
}

const PickupPointSelector: FunctionComponent<
  React.PropsWithChildren<PickupPointSelectorProps>
> = ({
  pickupPoints,
  maxDistance,
  onAddressSelected,
  searchAddress,
  userAddresses,
  pickupPointsIsLoading,
  carriers,
  onSelectPickupPoint,
  selectedPickupPointId,
  sellerId,
  noPickupPoints = false,
}) => {
  const isMobileView = useMedia(media.maxWidth.xs);

  const cardRefs: any = {};

  const selectPickupPointOnMap = (selectedPickupPointOnMap: PickupPoint) => {
    const cardElement = cardRefs[selectedPickupPointOnMap.id];

    if (cardElement) {
      cardElement.scrollIntoView();
    }
    onSelectPickupPoint(selectedPickupPointOnMap);
  };

  const carrierBrands = carriers?.map((carrier) => carrier.carrierBrand);

  const productTypes = carriers?.map((carrier) => carrier.productType);

  const enrichedPickupPoints = productTypes
    ?.map((productType) => {
      return carrierBrands
        ?.map((carrier = '') => {
          const points = pickupPoints?.[productType]?.[carrier]?.points ?? [];

          return points.map((pickupPoint) => {
            return {
              ...pickupPoint,
              ...(carriers?.find(
                (carrierItem) =>
                  carrierItem.carrierBrand === carrier &&
                  carrierItem.productType === productType,
              ) ?? {}),
            };
          });
        })
        .flat();
    })
    .flat();

  const filteredEnrichedPickupPoints = enrichedPickupPoints?.filter(
    (enrichedPickupPoint) =>
      enrichedPickupPoint?.latitude && enrichedPickupPoint?.longitude,
  );

  const filteredPickupPoints = uniqBy(filteredEnrichedPickupPoints, 'id').sort(
    (a, b) => (a?.distanceInMeters ?? 0) - (b?.distanceInMeters ?? 0),
  );

  const handleSetSelectedPickupPoint = (pickupPointsId: string) => {
    const pickupPointChoice = filteredPickupPoints?.find(
      (pickupPoint: any) => pickupPoint.id === pickupPointsId,
    );

    onSelectPickupPoint(pickupPointChoice as PickupPoint);
  };

  return (
    <$SelectContainerGrid fluid>
      <$SelectContainerRow>
        <$ColListContainer sm={4}>
          <$AddressContainer>
            <Address
              onAddressSelected={onAddressSelected}
              searchAddress={searchAddress}
              maxDistance={maxDistance}
              userAddresses={userAddresses}
            />
          </$AddressContainer>

          {pickupPointsIsLoading && <BaseLoader />}

          {!pickupPointsIsLoading && (
            <Row>
              {!noPickupPoints && (
                <PickupPointList
                  handleSetSelectedPickupPoint={handleSetSelectedPickupPoint}
                  pickupPoints={filteredPickupPoints as PickupPoint[]}
                  sellerId={sellerId}
                />
              )}

              {noPickupPoints && (
                <$NoPickupPoint>
                  <Banner
                    isOpen
                    hollow
                    notificationType={NOTIFICATION_TYPES.ALERT}
                  >
                    <SafeFormattedMessageWithoutSpread
                      message={messages.noPickupPoints}
                    />
                  </Banner>
                </$NoPickupPoint>
              )}
            </Row>
          )}
        </$ColListContainer>

        {pickupPointsIsLoading && !isMobileView ? (
          <BaseLoader />
        ) : (
          <$ColMapContainer sm={8}>
            <PickupPointMap
              searchAddress={searchAddress as any}
              selectedPickupPointId={selectedPickupPointId}
              onSelectPickupPoint={selectPickupPointOnMap as any}
              pickupPoints={filteredPickupPoints as any}
            />
          </$ColMapContainer>
        )}
      </$SelectContainerRow>
    </$SelectContainerGrid>
  );
};

export default PickupPointSelector;
